import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ClipboardList, Plus, CheckCircle2, Clock, AlertCircle, Eye, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { FormField } from "@/data/system-forms";
import { FormRenderer } from "@/components/forms/FormRenderer";
import { useFormAI } from "@/hooks/useFormAI";

interface JobFormsTabProps {
  jobId: string;
}

export function JobFormsTab({ jobId }: JobFormsTabProps) {
  const { orgId } = useAuth();
  const { prefillFormFromJob } = useFormAI();
  const [formSubmissions, setFormSubmissions] = useState<any[]>([]);
  const [formTemplates, setFormTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [viewFormOpen, setViewFormOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<any>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [fillFormOpen, setFillFormOpen] = useState(false);
  const [prefilledData, setPrefilledData] = useState<Record<string, any>>({});

  useEffect(() => {
    if (jobId && orgId) {
      loadFormSubmissions();
    }
  }, [jobId, orgId]);

  const loadFormSubmissions = async () => {
    try {
      setLoading(true);
      
      // Load existing submissions
      const { data, error } = await supabase
        .from("form_submissions")
        .select(`
          *,
          form_template:form_templates(*)
        `)
        .eq("job_id", jobId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      // Load available form templates from database
      const { data: templates, error: templatesError } = await supabase
        .from("form_templates")
        .select("*")
        .or(`org_id.eq.${orgId},is_system_template.eq.true`);
      
      if (templatesError) throw templatesError;
      
      console.log('Loaded form submissions:', data?.length, 'templates:', templates?.length);
      setFormSubmissions(data || []);
      setFormTemplates(templates || []);
    } catch (error) {
      console.error("Error loading form submissions:", error);
      toast.error("Failed to load forms");
    } finally {
      setLoading(false);
    }
  };

  const handleAddForm = async (templateId: string) => {
    const template = formTemplates.find((t) => t.id === templateId);
    if (!template) return;

    // AI pre-fill form with job data
    const prefilled = await prefillFormFromJob(jobId, template.form_fields);
    setPrefilledData(prefilled);
    setSelectedTemplate(template);
    setFillFormOpen(true);
    setAddFormOpen(false);
  };

  const handleSubmitForm = async (formData: Record<string, any>) => {
    try {
      if (selectedSubmission?.id) {
        // Update existing draft submission
        const { error } = await supabase
          .from("form_submissions")
          .update({
            submitted_data: formData,
            status: "submitted",
            submitted_at: new Date().toISOString(),
          })
          .eq("id", selectedSubmission.id);

        if (error) throw error;
      } else {
        // Create new submission
        const { error } = await supabase.from("form_submissions").insert({
          form_template_id: selectedTemplate.id,
          job_id: jobId,
          org_id: orgId,
          submitted_data: formData,
          status: "submitted",
          submitted_at: new Date().toISOString(),
        });

        if (error) throw error;
      }

      toast.success("Form submitted successfully");
      setFillFormOpen(false);
      setSelectedTemplate(null);
      setSelectedSubmission(null);
      loadFormSubmissions();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit form");
    }
  };

  const handleViewSubmission = (submission: any) => {
    setSelectedSubmission(submission);
    setViewFormOpen(true);
  };

  const handleDeleteSubmission = async (submissionId: string) => {
    if (!confirm("Are you sure you want to delete this form submission?")) return;

    try {
      const { error } = await supabase
        .from("form_submissions")
        .delete()
        .eq("id", submissionId);

      if (error) throw error;

      toast.success("Form submission deleted");
      loadFormSubmissions();
    } catch (error) {
      console.error("Error deleting submission:", error);
      toast.error("Failed to delete form submission");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "submitted":
        return "bg-green-500/10 text-green-500 border-green-500/20";
      case "approved":
        return "bg-blue-500/10 text-blue-500 border-blue-500/20";
      case "draft":
        return "bg-yellow-500/10 text-yellow-500 border-yellow-500/20";
      case "rejected":
        return "bg-red-500/10 text-red-500 border-red-500/20";
      default:
        return "bg-gray-500/10 text-gray-500 border-gray-500/20";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "submitted":
        return CheckCircle2;
      case "approved":
        return CheckCircle2;
      case "draft":
        return Clock;
      case "rejected":
        return AlertCircle;
      default:
        return Clock;
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      inspection: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      compliance: "bg-purple-500/10 text-purple-500 border-purple-500/20",
      customer: "bg-green-500/10 text-green-500 border-green-500/20",
      production: "bg-orange-500/10 text-orange-500 border-orange-500/20",
      insurance: "bg-red-500/10 text-red-500 border-red-500/20",
    };
    return colors[category] || "bg-gray-500/10 text-gray-500 border-gray-500/20";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading forms...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Job Forms</h3>
          <p className="text-sm text-muted-foreground">
            Required forms and documentation for this job
          </p>
        </div>
        <Button onClick={() => setAddFormOpen(true)} className="gap-2">
          <Plus className="w-4 h-4" />
          Add Form
        </Button>
      </div>

      {/* Forms List */}
      {formSubmissions.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ClipboardList className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">No forms yet</p>
            <p className="text-sm text-muted-foreground mb-4">
              Add required forms to document this job
            </p>
            <Button onClick={() => setAddFormOpen(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              Add Your First Form
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {formSubmissions.map((submission) => {
            const StatusIcon = getStatusIcon(submission.status);
            return (
              <Card key={submission.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <ClipboardList className="w-5 h-5 text-primary" />
                        <CardTitle className="text-lg">
                          {submission.form_template?.name || "Unknown Form"}
                        </CardTitle>
                      </div>
                      <CardDescription>
                        {submission.form_template?.description}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getCategoryColor(submission.form_template?.category)}>
                        {submission.form_template?.category}
                      </Badge>
                      <Badge className={getStatusColor(submission.status)}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {submission.status}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      Submitted: {new Date(submission.submitted_at || submission.created_at).toLocaleDateString()}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewSubmission(submission)}
                        className="gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </Button>
                      {submission.status === 'draft' && (
                        <Button
                          size="sm"
                          onClick={async () => {
                            const template = submission.form_template;
                            if (!template) return;
                            const prefilled = await prefillFormFromJob(jobId, template.form_fields);
                            setPrefilledData({...prefilled, ...submission.submitted_data});
                            setSelectedTemplate(template);
                            setSelectedSubmission(submission);
                            setFillFormOpen(true);
                          }}
                          className="gap-2"
                        >
                          <ClipboardList className="w-4 h-4" />
                          Fill Out
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteSubmission(submission.id)}
                        className="text-destructive gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Form Dialog */}
      <Dialog open={addFormOpen} onOpenChange={setAddFormOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Add Form to Job</DialogTitle>
            <DialogDescription>
              Select a form template to fill out for this job
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[500px] pr-4">
            <div className="grid gap-3">
              {formTemplates.map((template) => (
                <Card
                  key={template.id}
                  className="cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => handleAddForm(template.id)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <ClipboardList className="w-4 h-4 text-primary" />
                          <CardTitle className="text-base">{template.name}</CardTitle>
                        </div>
                        <CardDescription className="text-sm">
                          {template.description}
                        </CardDescription>
                      </div>
                      <Badge className={getCategoryColor(template.category)}>
                        {template.category}
                      </Badge>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Fill Form Dialog */}
      <Dialog open={fillFormOpen} onOpenChange={setFillFormOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Fill Out Form</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[calc(90vh-100px)] pr-4">
            {selectedTemplate && (
              <FormRenderer
                formName={selectedTemplate.name}
                formDescription={selectedTemplate.description}
                fields={selectedTemplate.form_fields}
                initialData={prefilledData}
                onSubmit={handleSubmitForm}
              />
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* View Submission Dialog */}
      <Dialog open={viewFormOpen} onOpenChange={setViewFormOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Form Submission</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[calc(90vh-100px)] pr-4">
            {selectedSubmission && (
              <FormRenderer
                formName={selectedSubmission.form_template?.name}
                formDescription={selectedSubmission.form_template?.description}
                fields={selectedSubmission.form_template?.form_fields || []}
                initialData={selectedSubmission.submitted_data}
                onSubmit={() => {}}
                readOnly={true}
              />
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
